import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { ModalComponent } from 'core/components/modal/modal.component';
import { SubscriptionsService, ProfileService, UtilsService, AgentsService, DistributorsService } from 'core/services';
import { Client, Distributor } from 'core/models';
import { EntityQuestion, QuestionBase } from 'core/components/forms';
import { PaymentsService } from '../../payments/services/payments.service';
import { BrokerCreditsDealerService } from '../../credits/broker-credits-dealer.service';
import { CLIENT_DETAIL_BUTTON_ACTIONS } from '../assets/constants';
import { ROLES } from 'core/services/assets/constants';
import { ClientDeleteComponent } from '../client-delete/client-delete.component';
import { ContextService } from 'core/services/context/context.service';

@Component({
  selector: 'lms-client-detail',
  templateUrl: './client-detail.component.html',
  styleUrls: ['./client-detail.component.scss']
})
export class ClientDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('ownerAgentSelector', { static: false }) public ownerAgentSelector: ModalComponent;
  @ViewChild('clientDelete', { static: false }) public clientDelete: ClientDeleteComponent;
  public client: Client;
  public clientDistributor: Distributor;
  public catalogueData: any;
  public tabsManager: any | Subscription = new Subscription();
  public clientStudentslistId = 'CLIENT_STUDENTS_LIST';
  public clientGroupslistId = 'CLIENT_GROUPS_LIST';
  public clientCreditslistId = 'CLIENT_CREDITS_LIST';
  public agentsClientlistId = 'ADMINS_LIST';

  public filters: any;
  public creditsFieldNames: any = [
    'clientId',
    'totalCredits',
    'initialDate',
    'expirationDate',
    'gracePeriod',
    'duration'
  ];
  public tabsToLoad: Map<string, string> = new Map<string, string>()
    .set('classes', this.clientGroupslistId)
    .set('agents', this.agentsClientlistId)
    .set('students', this.clientStudentslistId)
    .set('credits', this.clientCreditslistId);

  public subscriptionsCreditsEnabled = true;
  public subscriptionsUsersEnabled = true;

  public userRole: any;
  public updatedCredits: boolean = false;
  ownerAgentQuestion: QuestionBase<any>[] = [];
  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;
  private downloadClientCreditsReportSubscription: Subscription = new Subscription();
  public buttonList: { key: string; value: string; profile: string }[] = [];
  public errorSaveSubscription: object;
  public selectedTab = 0;

  constructor(
    public subscriptionsService: SubscriptionsService,
    public agentsService: AgentsService,
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public translate: TranslateService,
    public titleService: Title,
    public contextService: ContextService,
    public profileService: ProfileService,
    public paymentsService: PaymentsService,
    public distributorsService: DistributorsService,
    public dealerService: BrokerCreditsDealerService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.client = data['client'];
      const catalogue = data['courses'];
      this.catalogueData = {
        id: catalogue.id,
        defaultAccessMode: catalogue.defaultAccessMode,
        configuration: {
          shouldSendDiploma: catalogue.configuration.shouldSendDiploma
        }
      };
      this.filters = { clientId: this.client.id };
      this.titleService.setTitle(
        this.client.name +
          ' - ' +
          this.translate.instant('pageTitle.clients') +
          ' - ' +
          this.translate.instant('pageTitle.gamelearn')
      );
      this.setSubscriptonsTabs();
      this.buttonList = this.getActionListBtn(this.client);
    });
    if (this.client.partner) {
      this.clientDistributor = this.client.partner;
      this.clientDistributor.id = this.clientDistributor._id;
    }
  }

  public ngAfterViewInit(): void {
    this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
  }

  public ngOnDestroy(): void {
    this.tabsManager.unsubscribe();
    this.downloadClientCreditsReportSubscription.unsubscribe();
  }

  public setTab(selectedTab: MatTabChangeEvent): void {
    this.tabsManager.setTab(selectedTab);
    if (this.tabsToLoad.has(selectedTab['tab']['textLabel'])) {
      this.tabsToLoad.delete(selectedTab['tab']['textLabel']);
    }
  }

  public goBack(): void {
    this.location.back();
  }

  public async onUpdateClient(client: Client): Promise<void> {
    this.contextService.setClientPermissions(client);
    // force repaint component
    this.client = null;
    this.changeDetectorRef.detectChanges();
    this.client = client;
    this.setSubscriptonsTabs();
  }

  public onUpdateSsoConfig(ssoConfig: any): void {
    this.client.ssoEnabled = !!(ssoConfig && ssoConfig.enabled && environment.sids.campus === ssoConfig.sid);
  }

  public setSubscriptonsTabs(): void {
    this.subscriptionsCreditsEnabled =
      this.userRole.group === ROLES.CS || this.userRole.name === ROLES.SUPER_ADMIN
        ? true
        : this.client.subscriptions.credits;
    this.subscriptionsUsersEnabled =
      this.userRole.group === ROLES.CS ||
      this.userRole.name === ROLES.SUPER_ADMIN ||
      this.userRole.name === ROLES.DISTRIBUTOR_MANAGER
        ? true
        : this.client.subscriptions.users;
  }

  public openOwnerAgentSelector(): void {
    const filters =
      this.client.distributorId && this.client.partner.clientId === this.client.id
        ? { distributorId: this.client.distributorId, role: this.client.partner.type }
        : { clientIds: this.client.id, role: 'clientAdmin' };

    this.ownerAgentQuestion = [
      // TODO change to DropdownQuestion
      new EntityQuestion({
        key: 'owner',
        label: 'nouns.agent',
        title: 'actions.selectAdministrator',
        entityOptions: {
          preload: true,
          filters: filters
        },
        dataProvider: this.agentsService,
        eventHandlers: {
          onFocus: (e, question) => {
            question.open = true;
          }
        }
      })
    ];
    this.ownerAgentSelector.open();
  }

  public onUpdateDistributor(distributor: Distributor): void {
    this.clientDistributor = distributor;
  }

  public onTransactionCompleted(): void {
    this.distributorsService.getDistributor(this.clientDistributor.id).subscribe((distributor) => {
      this.onUpdateDistributor(distributor);
    });
    this.updatedCredits = !this.updatedCredits;
  }

  public stripe(data): void {
    this.ownerAgentSelector.close();
    const stripeCustomer = {
      clientId: this.client.id,
      adminId: data.owner
    };
    this.paymentsService.createCustomerAndSubscription(stripeCustomer).subscribe((updatedClient) => {
      this.client = { ...this.client, ...updatedClient };
    });
  }

  public downloadClientCreditsReport(): void {
    this.downloadClientCreditsReportSubscription = this.subscriptionsService
      .downloadClientCreditsReport(this.client.id)
      .subscribe((res) => {
        const clientName = this.client.name.split(' ').join('');

        saveAs(res, `CreditsReport_${clientName}.csv`);
      });
  }

  public onDelete(): void {
    this.router.navigate(['/clients'], { replaceUrl: true });
  }

  /**
   * Show error from the form
   * @param errorEvent
   */
  public setErrorMessage(errorEvent: object): void {
    this.errorSaveSubscription = errorEvent;
  }

  /**
   * Get the list of action buttons
   * @param client
   * @private
   */
  private getActionListBtn(client: Client): { key: string; value: string; profile: string }[] {
    return CLIENT_DETAIL_BUTTON_ACTIONS.options.filter((btn) => {
      switch (btn.key) {
        case 'deleteInfo':
          return client.hasActiveData && this.profileService.canExecute(btn.profile);
        // 'stripe' and 'delete':
        default:
          return this.profileService.canExecute(btn.profile);
      }
    });
  }

  /**
   * The selected option of the action/dots button
   * @param key
   */
  public selectAction(key) {
    switch (key) {
      case 'stripe':
        this.openOwnerAgentSelector();
        break;
      case 'deleteInfo':
      case 'delete':
        this.openDeleteComponent(key);
        break;
    }
  }

  private openDeleteComponent(action: string) {
    this.clientDelete.open(action);
  }
}
